import { Dispatch, SetStateAction, useCallback, useState } from 'react'
import { useTranslations } from 'next-intl'
import { useQueryClient } from '@tanstack/react-query'
import { Toast } from '@skop-app/lib-front'
import { v7 as uuid } from 'uuid'

import {
  getProjectReadonlyMessageKey,
  type ProjectPermissions,
} from '@/features/project/project.permissions'
import { useUpsertCatalog } from '@/services/mutations/catalog'
import { Catalog, CatalogBlock, Context } from '@/types'
import {
  normalizeCatalogLotHeaders,
  serializeCatalogBlocks,
} from '@/utils/catalog.handler'
import { resolveCatalogLanguageSettings } from '@/utils/languagePolicy'

interface UseCatalogVersioningProps {
  catalogs: Catalog[]
  projectID: string
  blocks: CatalogBlock[]
  catalogContext?: Context
  setHasUnsavedCatalogDraft: Dispatch<SetStateAction<boolean>>
  selectSavedCatalog: (catalogId: string) => void
  date?: string
  supportedLocales?: string[]
  defaultLocale?: string
  firstPageNumber?: number
  rotorFooterLabel?: string
  projectPermissions?: ProjectPermissions
  onSaveSuccess?: (catalogId: string) => void
}

export const useCatalogVersioning = ({
  catalogs,
  projectID,
  blocks,
  catalogContext,
  setHasUnsavedCatalogDraft,
  selectSavedCatalog,
  date,
  supportedLocales = ['fr'],
  defaultLocale = 'fr',
  firstPageNumber = 1,
  rotorFooterLabel,
  projectPermissions,
  onSaveSuccess,
}: UseCatalogVersioningProps) => {
  const t = useTranslations()
  const tProjects = useTranslations('Projects')
  const queryClient = useQueryClient()
  const { mutateAsync: upsertCatalog } = useUpsertCatalog()

  const [isSaving, setIsSaving] = useState(false)
  const canEditCatalog = projectPermissions?.canEditCatalog ?? true
  const readonlyMessageKey = projectPermissions
    ? getProjectReadonlyMessageKey(projectPermissions)
    : undefined

  const handleSave = useCallback(async () => {
    if (!canEditCatalog) {
      if (readonlyMessageKey) {
        Toast.error(tProjects(readonlyMessageKey))
      }
      return
    }

    setIsSaving(true)
    try {
      // Validate required fields
      if (!date) {
        Toast.error(t('Catalog.date-required'))
        return
      }

      if (!blocks || blocks.length === 0) {
        Toast.error(t('Catalog.no-content'))
        return
      }

      const normalizedBlocks = normalizeCatalogLotHeaders(
        blocks,
        catalogContext,
      )

      // Calculate version number - get the highest version from existing catalogs
      const maxVersion = catalogs.reduce((max, cat) => {
        return Math.max(max, cat.version || 0)
      }, 0)

      const newVersion = maxVersion + 1
      const languageSettings = resolveCatalogLanguageSettings({
        supportedLocales,
        defaultLocale,
      })

      // Always create a new catalog for versioning
      const input = {
        id: uuid(),
        projectCatalogsId: projectID,
        content: [
          {
            version: '1.0',
            blocks: serializeCatalogBlocks(normalizedBlocks),
          },
        ],
        lastUpdated: new Date().toISOString(),
        publishDate: new Date(date).toISOString(),
        createdAt: new Date().toISOString(),
        metadata: [
          {
            key: 'version',
            type: 'number',
            value: newVersion.toString(),
          },
          {
            key: 'createdAt',
            type: 'datetime',
            value: new Date().toISOString(),
          },
          {
            key: 'supportedLocales',
            type: 'array',
            value: JSON.stringify(languageSettings.supportedLocales),
          },
          {
            key: 'defaultLocale',
            type: 'string',
            value: languageSettings.defaultLocale,
          },
          {
            key: 'firstPageNumber',
            type: 'number',
            value: firstPageNumber.toString(),
          },
          ...(rotorFooterLabel
            ? [
                {
                  key: 'rotorFooterLabel',
                  type: 'string',
                  value: rotorFooterLabel,
                } as const,
              ]
            : []),
        ],
      }

      await upsertCatalog({ upsertType: 'create', input })
      Toast.success(t('Catalog.saved-version', { version: newVersion }))

      // Update local state to reflect saved version
      if (onSaveSuccess) {
        onSaveSuccess(input.id)
      } else {
        setHasUnsavedCatalogDraft(false)
        selectSavedCatalog(input.id)
      }

      // Refresh catalogs list (key must match useCatalogs)
      queryClient.invalidateQueries({
        queryKey: ['catalogs', projectID],
      })
    } catch (error) {
      Toast.error(t('Catalog.error'))
      console.error('Error saving catalog:', error)
    } finally {
      setIsSaving(false)
    }
  }, [
    date,
    blocks,
    catalogContext,
    catalogs,
    projectID,
    upsertCatalog,
    t,
    setHasUnsavedCatalogDraft,
    selectSavedCatalog,
    queryClient,
    supportedLocales,
    defaultLocale,
    firstPageNumber,
    rotorFooterLabel,
    canEditCatalog,
    readonlyMessageKey,
    tProjects,
    onSaveSuccess,
  ])

  return {
    isSaving,
    handleSave,
  }
}
